import { useMemo, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { BarChart3, ChevronLeft } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { catalogEntries, namespaceOf } from './catalogEntries'

type Target = 'ru' | 'uz'
const LANGS: Target[] = ['ru', 'uz']

function pct(translated: number, total: number): number {
  return total ? Math.round((translated / total) * 100) : 100
}

/**
 * A22.14 — Tarjima statistikasi. Til bo'yicha umumiy qamrov + nom-maydon (namespace)
 * bo'yicha taqsimot. "Tarjimasiz" = qiymati en bilan bir xil (evristika). Backend YO'Q.
 */
export function LocalizationStats() {
  const t = useT()
  const { total, perLang, perNs } = useMemo(() => {
    const rows = catalogEntries().filter((e) => !e.fn && e.en !== '')
    const perLang = LANGS.map((l) => {
      const translated = rows.filter((e) => e[l] !== e.en).length
      return { lang: l, translated, total: rows.length, pct: pct(translated, rows.length) }
    })
    const nsMap = new Map<string, { total: number; ru: number; uz: number }>()
    for (const e of rows) {
      const ns = namespaceOf(e.key)
      const cur = nsMap.get(ns) ?? { total: 0, ru: 0, uz: 0 }
      cur.total++
      if (e.ru !== e.en) cur.ru++
      if (e.uz !== e.en) cur.uz++
      nsMap.set(ns, cur)
    }
    const perNs = [...nsMap.entries()]
      .map(([ns, v]) => ({ ns, ...v, ruPct: pct(v.ru, v.total), uzPct: pct(v.uz, v.total) }))
      .sort((a, b) => b.total - a.total)
    return { total: rows.length, perLang, perNs }
  }, [])

  return (
    <>
      <PageHeader
        title={t.loc.statsTitle}
        screenId="A22.14"
        actions={
          <Link
            to="/localization"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.loc.title}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="mb-4 flex flex-wrap gap-3">
          <StatTile label={t.loc.keys} value={total.toLocaleString()} icon={<BarChart3 size={18} className="text-accent" />} />
          {perLang.map((r) => (
            <StatTile key={r.lang} label={r.lang.toUpperCase()} value={`${r.pct}%`} sub={`${r.translated}/${r.total}`} />
          ))}
        </div>

        <div className="overflow-hidden rounded-md border border-border-default">
          <div className="grid grid-cols-[minmax(120px,1fr)_70px_1fr_1fr] gap-3 border-b border-border-default bg-surface-2 px-3 py-2 text-xs font-medium text-tertiary">
            <span>{t.loc.namespace}</span>
            <span className="text-right">{t.loc.keys}</span>
            <span>RU</span>
            <span>UZ</span>
          </div>
          {perNs.map((r) => (
            <div
              key={r.ns}
              className="grid grid-cols-[minmax(120px,1fr)_70px_1fr_1fr] items-center gap-3 border-b border-border-subtle px-3 py-1.5 text-sm last:border-0"
            >
              <span className="truncate font-mono text-xs text-accent">{r.ns}</span>
              <span className="text-right font-mono tabular text-secondary">{r.total}</span>
              <Bar pct={r.ruPct} />
              <Bar pct={r.uzPct} />
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

function StatTile({ label, value, sub, icon }: { label: string; value: string; sub?: string; icon?: ReactNode }) {
  return (
    <div className="inline-flex items-center gap-3 rounded-lg border border-border-default bg-surface-2 px-4 py-3">
      {icon}
      <div>
        <div className="text-xs text-tertiary">{label}</div>
        <div className="font-mono text-xl tabular text-primary">{value}</div>
        {sub ? <div className="text-xs text-tertiary">{sub}</div> : null}
      </div>
    </div>
  )
}

function Bar({ pct }: { pct: number }) {
  return (
    <span className="flex items-center gap-2">
      <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-3">
        <span className="block h-full rounded-full bg-accent" style={{ width: `${pct}%` }} />
      </span>
      <span className="w-9 text-right font-mono text-xs tabular text-tertiary">{pct}%</span>
    </span>
  )
}
